import React from 'react';
import { Badge } from '@/components/ui/badge';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import { useEnvironment, type EnvironmentMode } from '@/contexts/EnvironmentContext';
import { cn } from '@/lib/utils';
import { FlaskConical, TestTube, Zap } from 'lucide-react';

const getEnvironmentStyles = (mode: EnvironmentMode) => {
  switch (mode) {
    case 'demo':
      return {
        className: 'bg-blue-100 text-blue-700 border-blue-200 hover:bg-blue-100',
        icon: FlaskConical,
        description: 'Fully simulated environment with mock data. No real exchange connections.',
      };
    case 'testnet':
      return {
        className: 'bg-amber-100 text-amber-700 border-amber-200 hover:bg-amber-100',
        icon: TestTube,
        description: 'Connected to exchange testnets/sandboxes. Real APIs, fake money.',
      };
    case 'prod':
      return {
        className: 'bg-green-100 text-green-700 border-green-200 hover:bg-green-100',
        icon: Zap,
        description: 'Production environment. Real money trading - use with caution.',
      };
    default:
      return {
        className: 'bg-slate-100 text-slate-700 border-slate-200 hover:bg-slate-100',
        icon: FlaskConical,
        description: 'Unknown environment mode.',
      };
  }
};

export function EnvironmentBadge() {
  const { mode, modeLabel } = useEnvironment();
  const styles = getEnvironmentStyles(mode);
  const Icon = styles.icon;

  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <Badge
          variant="outline"
          className={cn(
            'h-7 px-2.5 text-xs font-medium cursor-default select-none',
            styles.className
          )}
          data-testid="environment-badge"
        >
          <Icon className="h-3.5 w-3.5 mr-1.5" />
          {modeLabel}
        </Badge>
      </TooltipTrigger>
      <TooltipContent side="bottom" className="max-w-xs">
        <p className="text-sm">{styles.description}</p>
      </TooltipContent>
    </Tooltip>
  );
}
